import { Id } from '@/infra/id'
import { Model, type ModelProps } from '@/infra/model'
import { unixEpoch } from '@/utils/date-util'
import { computed, observable } from 'mobx'
import { User } from '@/features/iam/users/User'

export type JobScopeChangeProps = ModelProps<JobScopeChange>
export type JobScopeChangePriceBreakdownProps =
  ModelProps<JobScopeChangePriceBreakdown>

export interface JobScopeChangeItem {
  id: Id
  name: string
  quantity: number
  inventoryItemId: Id
  inventoryItemVersionId: Id
  inventoryItemType: InventoryItemType
}

export interface JobScopeChangeItemPriceBreakdown {
  inventoryItemVersionId: Id
  name: string
  description: string
  previousQuantity: number
  newQuantity: number
  previousPrice: number
  newPrice: number
}

export class JobScopeChangePriceBreakdown extends Model<JobScopeChangePriceBreakdown> {
  static readonly none = new JobScopeChangePriceBreakdown()

  @observable
  accessor id = Id.none

  @observable
  accessor status: JobScopeChangeStatus = 'Unspecified'

  @observable
  accessor items: JobScopeChangeItemPriceBreakdown[] = []

  @observable
  accessor previousScopePrice = 0

  @observable
  accessor newScopePrice = 0

  @observable
  accessor respondedAt: Date | null = null

  /**
   * The amount due for the client to pay.
   */
  @computed
  get amountDue() {
    return this.newScopePrice - this.previousScopePrice
  }
}

export class JobScopeChange extends Model<JobScopeChange> {
  static readonly none = new JobScopeChange()

  @observable
  accessor id = Id.none

  @observable
  accessor createdAt = unixEpoch()

  @observable
  accessor jobId = Id.none

  @observable
  accessor status: JobScopeChangeStatus = 'PendingAdminApproval'

  @observable
  accessor items: JobScopeChangeItem[] = []

  @observable
  accessor createdBy: User = User.none

  /**
   * Whether the job scope change is approved by the admin.
   */
  @computed
  get isAdminApproved() {
    return (
      this.status !== 'PendingFinalization' &&
      this.status !== 'PendingAdminApproval'
    )
  }
}

export type JobScopeChangeStatus = (typeof JobScopeChangeStatuses)[number]

export const JobScopeChangeStatuses = [
  'PendingFinalization',
  'PendingAdminApproval',
  'PendingClientApproval',
  'Accepted',
  'Rejected',
  'ApplyFailed',
  'Unspecified',
] as const

export type InventoryItemType = (typeof InventoryItemTypes)[number]

export const InventoryItemTypes = [
  'Default',
  'Bookkeeping',
  'Consultation',
  'Tax',
  'Extension',
  'Unspecified',
] as const
